import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React from 'react';
import { Link } from 'react-router-dom';

interface CaseStudy {
  image: string;
  logoAlt: string;
  organization: string;
  eyebrow: string;
  title: string;
  summary: React.ReactNode;
  outcome: string;
  link: string;
}

const caseStudies: CaseStudy[] = [
  {
    image: '/assets/afrl_logo.webp',
    logoAlt: 'Air Force Research Laboratory logo',
    organization: 'AFRL',
    eyebrow: 'Research and analysis',
    title: 'Key Research and Analysis Software',
    summary:
      'In active use by the Air Force Research Laboratory (AFRL) for research and analysis, particularly at the Turbine Engine Fatigue Facility.',
    outcome: 'Research-grade as-manufactured modeling workflows for turbine engine analysis.',
    link: '/case-studies/afrl',
  },
  {
    image: '/assets/pratt_whitney_logo.webp',
    logoAlt: 'Pratt and Whitney logo',
    organization: 'Pratt & Whitney',
    eyebrow: 'Commercial transition',
    title: 'State of the Art Quality Control',
    summary:
      'First DoD commercial license of software under Section 801. FEMORPH provides advanced modeling and simulation capabilities to assess irregularities in turbine blades, resulting in cost savings and improved readiness.',
    outcome: 'Commercial transition of DoD software into production aerospace quality control.',
    link: '/case-studies/pratt-whitney',
  },
  {
    image: '/assets/bdc_logo.svg',
    logoAlt: 'Blade Diagnostics logo',
    organization: 'Blade Diagnostics',
    eyebrow: 'Sustainment',
    title: 'IBR Sustainment',
    summary:
      "Deployed in support of IBR sustainment by the Air Force Life Cycle Management Center (AFLCMC). Software used in conjunction with BDC's advanced techniques to predict IBR blade mode shapes due to changes in geometry caused by blade blending in production workflows at Tinker Air Force Base.",
    outcome: 'Depot sustainment workflows for repaired integrally bladed rotors.',
    link: '/case-studies/bdc',
  },
];

const CaseStudies: React.FC = () => {
  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <Box component="section" sx={{ bgcolor: '#172126', color: 'white' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
          <Grid container spacing={{ xs: 3, md: 6 }} alignItems="end">
            <Grid item xs={12} md={8}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.72)' }}>
                Fielded FEMORPH workflows
              </Typography>
              <Typography variant="h2" sx={{ mt: 1, mb: 2, maxWidth: 760 }}>
                Case Studies
              </Typography>
              <Typography variant="h6" sx={{ maxWidth: 760, color: 'rgba(255,255,255,0.78)', fontWeight: 400, lineHeight: 1.6 }}>
                Applied examples of as-manufactured model creation, commercial transition, and sustainment workflows in aerospace environments.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                <Chip label="Aerospace" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.28)' }} variant="outlined" />
                <Chip label="Production workflows" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.28)' }} variant="outlined" />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
        <Grid container spacing={3}>
          {caseStudies.map((caseStudy) => (
            <Grid item xs={12} md={4} key={caseStudy.title}>
              <Card
                component={Link}
                to={caseStudy.link}
                aria-label={`View ${caseStudy.title} case study`}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: 'none',
                  color: 'text.primary',
                  textDecoration: 'none',
                  transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                  '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: '0 18px 36px rgba(23, 33, 38, 0.12)',
                    transform: 'translateY(-3px)',
                  },
                  '&:focus-visible': {
                    outline: '3px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: 3,
                  },
                  '&:hover .case-study-logo': {
                    bgcolor: '#eef3f1',
                  },
                  '&:hover .case-study-action': {
                    backgroundColor: '#dbe9ec',
                    color: '#0b3848',
                  },
                  '&:hover .case-study-action svg': {
                    transform: 'translateX(4px)',
                  },
                }}
              >
                <Box
                  className="case-study-logo"
                  sx={{
                    height: { xs: 132, md: 142 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#f8faf8',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    p: 3,
                    transition: 'background-color 160ms ease',
                  }}
                >
                  <CardMedia
                    component="img"
                    image={caseStudy.image}
                    alt={caseStudy.logoAlt}
                    sx={{ maxHeight: { xs: 82, md: 92 }, maxWidth: '76%', objectFit: 'contain' }}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="overline" color="secondary.dark">
                      {caseStudy.eyebrow}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      {caseStudy.organization}
                    </Typography>
                  </Stack>
                  <Typography gutterBottom variant="h5" component="h2" sx={{ mt: 1 }}>
                    {caseStudy.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    {caseStudy.summary}
                  </Typography>
                  <Box sx={{ mt: 2.5, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 700, textTransform: 'uppercase' }}>
                      Outcome
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                      {caseStudy.outcome}
                    </Typography>
                  </Box>
                </CardContent>
                <Stack
                  className="case-study-action"
                  direction="row"
                  spacing={1}
                  sx={{
                    mt: 'auto',
                    px: 3,
                    py: 2,
                    alignItems: 'center',
                    color: 'primary.main',
                    fontWeight: 700,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    transition: 'background-color 160ms ease, color 160ms ease',
                  }}
                >
                  <Typography variant="button" component="span">
                    View case study
                  </Typography>
                  <ArrowForwardIcon sx={{ color: 'inherit', fontSize: 18, transition: 'transform 160ms ease' }} />
                </Stack>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default CaseStudies;
